import React, {useEffect, useId, useRef, useState} from "react";
import {Overlay} from "react-bootstrap";

/**
 * Selbst gestaltete Auswahlliste als Ersatz für ein natives <select>. Im Haus
 * gilt: keine browsereigenen Bedienelemente, damit die Optik auf jedem
 * System gleich aussieht und zum CI passt (siehe globale Vorgabe).
 *
 * Die Positionierung des aufklappenden Listenfelds übernimmt react-bootstrap
 * "Overlay" (Popper unter der Haube, bereits Projektabhängigkeit) - das
 * erledigt Portal, Flip an Bildschirmrändern und Schließen bei Klick nach
 * außen (rootClose), ohne das selbst nachbauen zu müssen. Gerendert wird
 * dabei ausschließlich eigenes Markup (role="listbox"/"option"), kein
 * natives Formularelement.
 *
 * @param {string} [id] - Optionale ID des Auslöser-Elements (a11y-Kopplung).
 * @param {string|number} value - Aktuell gewählter Wert.
 * @param {{value: string|number, label: string}[]} options - Auswahlmöglichkeiten.
 * @param {(value: string|number) => void} onChange
 * @param {"sm"|undefined} [size] - Größe, angelehnt an Bootstrap-Formularfelder.
 * @param {string} [ariaLabel]
 * @param {boolean} [disabled]
 * @param {string} [className]
 * @param {object} [style]
 */
export default function SelectList({
    id,
    value,
    options,
    onChange,
    size,
    ariaLabel,
    disabled = false,
    className = "",
    style,
}) {
    const generatedId = useId();
    const instanceId = id || `select-list-${generatedId}`;
    const triggerRef = useRef(null);
    const listRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const selectedIndex = options.findIndex((opt) => String(opt.value) === String(value ?? ""));
    const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

    const openList = (startIndex) => {
        setActiveIndex(startIndex >= 0 ? startIndex : Math.max(selectedIndex, 0));
        setOpen(true);
    };

    const closeList = (refocus = true) => {
        setOpen(false);
        if (refocus) {
            triggerRef.current?.focus();
        }
    };

    const commitIndex = (index) => {
        const opt = options[index];
        if (opt) {
            onChange(opt.value);
        }
        closeList();
    };

    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }

        if (!open) {
            if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
                e.preventDefault();
                openList(selectedIndex);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((i) => Math.min(options.length - 1, i + 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((i) => Math.max(0, i - 1));
                break;
            case "Home":
                e.preventDefault();
                setActiveIndex(0);
                break;
            case "End":
                e.preventDefault();
                setActiveIndex(options.length - 1);
                break;
            case "Enter":
                e.preventDefault();
                commitIndex(activeIndex);
                break;
            case "Escape":
                e.preventDefault();
                closeList();
                break;
            case "Tab":
                closeList(false);
                break;
            default:
                break;
        }
    };

    // Aktiven Eintrag beim Blättern per Tastatur in Sicht scrollen.
    useEffect(() => {
        if (!open) {
            return;
        }
        const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
        el?.scrollIntoView({block: "nearest"});
    }, [open, activeIndex]);

    const sizeClass = size === "sm" ? " form-select-sm" : "";

    return (
        <>
            <button
                type="button"
                ref={triggerRef}
                id={instanceId}
                className={`form-select text-start${sizeClass}${className ? " " + className : ""}`}
                style={{cursor: disabled ? "default" : "pointer", ...style}}
                disabled={disabled}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={`${instanceId}-listbox`}
                aria-activedescendant={open && activeIndex >= 0 ? `${instanceId}-option-${activeIndex}` : undefined}
                aria-label={ariaLabel}
                onClick={() => (open ? closeList() : openList(selectedIndex))}
                onKeyDown={handleKeyDown}
            >
                {selectedOption ? selectedOption.label : " "}
            </button>

            <Overlay
                target={triggerRef}
                show={open}
                placement="bottom-start"
                flip
                rootClose
                onHide={() => closeList(false)}
            >
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        id={`${instanceId}-listbox`}
                        role="listbox"
                        ref={(node) => {
                            listRef.current = node;
                            if (typeof overlayProps.ref === "function") {
                                overlayProps.ref(node);
                            }
                        }}
                        tabIndex={-1}
                        aria-label={ariaLabel}
                        className="bg-white border rounded-1 shadow-sm py-1"
                        style={{
                            ...overlayProps.style,
                            minWidth: triggerRef.current?.offsetWidth || 160,
                            maxHeight: 260,
                            overflowY: "auto",
                            zIndex: 3000,
                        }}
                        onKeyDown={handleKeyDown}
                    >
                        {options.map((opt, index) => (
                            <div
                                key={String(opt.value)}
                                id={`${instanceId}-option-${index}`}
                                data-index={index}
                                role="option"
                                aria-selected={index === selectedIndex}
                                className={`px-2 py-1 small${index === activeIndex ? " bg-primary text-white" : ""}`}
                                style={{cursor: "pointer"}}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    onChange(opt.value);
                                    closeList();
                                }}
                            >
                                {opt.label}
                            </div>
                        ))}
                    </div>
                )}
            </Overlay>
        </>
    );
}
